import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

type RequestType = 'deposit' | 'withdraw';

interface MoneyRequest {
  id: string;
  type: RequestType;
  user_id: string;
  user_name: string;
  amount: number;
  fee: number;
  status: string;
  bank_name: string;
  account_number: string;
  account_holder: string;
  transaction_code: string | null;
  proof_image: string | null;
  rejection_reason: string | null;
  created_at: string;
}

interface ProfileMap {
  [id: string]: string;
}

interface BankMap {
  [id: string]: { bank_name: string; account_number: string; account_holder: string };
}

const statusStyle: Record<string, string> = {
  pending: 'bg-accent-500/15 text-accent-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  processing: 'bg-amber-500/15 text-amber-500',
  rejected: 'bg-rose-500/15 text-rose-400',
};

const statusLabel: Record<string, string> = {
  pending: 'Chờ xử lý',
  approved: 'Đã duyệt',
  processing: 'Đang xử lý',
  rejected: 'Từ chối',
};

export default function AdminDeposits() {
  const [filter, setFilter] = useState<'all' | RequestType>('all');
  const [requests, setRequests] = useState<MoneyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [proofUrl, setProofUrl] = useState<{ id: string; url: string } | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: depositsRaw }, { data: withdrawalsRaw }, { data: profilesRaw }, { data: banksRaw }] = await Promise.all([
        supabase.rpc('get_all_deposit_requests_json'),
        supabase.rpc('get_all_withdraw_requests_json'),
        supabase.rpc('get_all_profiles_json'),
        supabase.rpc('get_all_bank_accounts_json'),
      ]);

      const deposits = Array.isArray(depositsRaw) ? depositsRaw : JSON.parse((depositsRaw as string) || '[]');
      const withdrawals = Array.isArray(withdrawalsRaw) ? withdrawalsRaw : JSON.parse((withdrawalsRaw as string) || '[]');
      const profilesData = Array.isArray(profilesRaw) ? profilesRaw : JSON.parse((profilesRaw as string) || '[]');
      const banksData = Array.isArray(banksRaw) ? banksRaw : JSON.parse((banksRaw as string) || '[]');

      const profileMap: ProfileMap = {};
      (profilesData || []).forEach((p: Record<string, unknown>) => {
        profileMap[p.id as string] = (p.full_name as string) || (p.username as string) || 'Người dùng';
      });

      const bankMap: BankMap = {};
      (banksData || []).forEach((b: Record<string, unknown>) => {
        bankMap[b.id as string] = { bank_name: b.bank_name as string, account_number: b.account_number as string, account_holder: b.account_holder as string };
      });

      const combined: MoneyRequest[] = [];

      (deposits || []).forEach((d) => {
        const bank = d.bank_account_id ? bankMap[d.bank_account_id] : undefined;
        combined.push({
          id: d.id,
          type: 'deposit',
          user_id: d.user_id,
          user_name: profileMap[d.user_id] || 'Người dùng',
          amount: Number(d.amount),
          fee: 0,
          status: d.status,
          bank_name: bank?.bank_name || '-',
          account_number: bank?.account_number || '-',
          account_holder: bank?.account_holder || '-',
          transaction_code: d.transaction_code,
          proof_image: d.proof_image,
          rejection_reason: d.rejection_reason,
          created_at: d.created_at,
        });
      });

      (withdrawals || []).forEach((w) => {
        const bank = w.bank_account_id ? bankMap[w.bank_account_id] : undefined;
        combined.push({
          id: w.id,
          type: 'withdraw',
          user_id: w.user_id,
          user_name: profileMap[w.user_id] || 'Người dùng',
          amount: Number(w.amount),
          fee: Number(w.fee),
          status: w.status,
          bank_name: bank?.bank_name || '-',
          account_number: bank?.account_number || '-',
          account_holder: bank?.account_holder || '-',
          transaction_code: null,
          proof_image: null,
          rejection_reason: w.rejection_reason,
          created_at: w.created_at,
        });
      });

      combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRequests(combined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải dữ liệu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = requests.filter((r) => filter === 'all' || r.type === filter);

  const viewProof = async (request: MoneyRequest) => {
    if (!request.proof_image) return;
    try {
      const { data, error: signedError } = await supabase.storage
        .from('private')
        .createSignedUrl(request.proof_image, 120);
      if (signedError) throw signedError;
      setProofUrl({ id: request.id, url: data.signedUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải ảnh chứng từ.');
    }
  };

  const handleApprove = async (request: MoneyRequest) => {
    setProcessing(request.id);
    setError('');
    setSuccess('');
    try {
      const { error: fnError } = await supabase.functions.invoke('review-money-request', {
        body: { request_type: request.type, request_id: request.id, action: 'approve' },
      });
      if (fnError) throw fnError;
      setSuccess('Đã duyệt thành công. Số dư ví đã được cập nhật.');
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Duyệt thất bại.');
    } finally {
      setProcessing(null);
    }
  };

  const openReject = (request: MoneyRequest) => {
    setRejecting(request.id);
    setRejectReason('');
  };

  const handleReject = async (request: MoneyRequest) => {
    setProcessing(request.id);
    setError('');
    setSuccess('');
    try {
      const { error: fnError } = await supabase.functions.invoke('review-money-request', {
        body: { request_type: request.type, request_id: request.id, action: 'reject', reason: rejectReason },
      });
      if (fnError) throw fnError;
      setSuccess('Đã từ chối yêu cầu.');
      setRejecting(null);
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Từ chối thất bại.');
    } finally {
      setProcessing(null);
    }
  };

  const handleProcess = async (request: MoneyRequest) => {
    setProcessing(request.id);
    setError('');
    setSuccess('');
    try {
      const { error: fnError } = await supabase.functions.invoke('review-money-request', {
        body: { request_type: 'withdraw', request_id: request.id, action: 'process' },
      });
      if (fnError) throw fnError;
      setSuccess('Đã đánh dấu chuyển khoản thực tế.');
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cập nhật thất bại.');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-6">Quản lý yêu cầu nạp / rút</h1>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
            <i className="ri-check-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{success}</p>
          </div>
        )}

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/60 w-fit mb-6">
          {[
            { key: 'all' as const, label: 'Tất cả' },
            { key: 'deposit' as const, label: 'Nạp tiền' },
            { key: 'withdraw' as const, label: 'Rút tiền' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${filter === t.key ? 'bg-accent-500 text-background-950' : 'text-foreground-600 hover:text-foreground-900'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-foreground-600">Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-background-200/60 bg-background-100 py-10 text-center text-sm text-foreground-600">
            <i className="ri-inbox-line text-3xl block mb-2 text-foreground-500"></i>
            Không có yêu cầu nào
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-background-200/60">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Người dùng</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Loại</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Số tiền</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Ngân hàng</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Chứng từ</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Trạng thái</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Thời gian</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={`${r.type}-${r.id}`} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3 font-semibold text-foreground-950 whitespace-nowrap">{r.user_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${r.type === 'deposit' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                        {r.type === 'deposit' ? 'Nạp tiền' : 'Rút tiền'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-900 whitespace-nowrap">
                      {r.amount.toLocaleString('vi-VN')}đ
                      {r.type === 'withdraw' && r.fee > 0 && (
                        <span className="block text-xs text-foreground-500 font-normal">Phí {r.fee.toLocaleString('vi-VN')}đ</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">
                      <span className="block">{r.bank_name}</span>
                      <span className="block text-xs text-foreground-500">{r.account_number}</span>
                      <span className="block text-xs text-foreground-500">{r.account_holder}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.type === 'deposit' ? (
                        <div className="space-y-1">
                          {r.transaction_code && (
                            <span className="block text-xs font-mono text-foreground-700">{r.transaction_code}</span>
                          )}
                          {r.proof_image ? (
                            <button onClick={() => viewProof(r)} className="text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap">
                              <i className="ri-image-line mr-1"></i>Xem ảnh
                            </button>
                          ) : (
                            <span className="text-xs text-foreground-500">—</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-foreground-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyle[r.status]}`}>
                        {statusLabel[r.status]}
                      </span>
                      {r.status === 'rejected' && r.rejection_reason && (
                        <p className="text-xs text-rose-400 mt-1">{r.rejection_reason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === 'pending' ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(r)}
                            disabled={processing === r.id}
                            className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap"
                          >
                            {processing === r.id && rejecting !== r.id ? 'Đang xử lý...' : 'Duyệt'}
                          </button>
                          <button
                            onClick={() => openReject(r)}
                            disabled={processing === r.id}
                            className="px-3 py-1.5 rounded-md bg-rose-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap"
                          >
                            Từ chối
                          </button>
                        </div>
                      ) : r.type === 'withdraw' && r.status === 'approved' ? (
                        <button
                          onClick={() => handleProcess(r)}
                          disabled={processing === r.id}
                          className="px-3 py-1.5 rounded-md bg-amber-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap"
                        >
                          {processing === r.id ? 'Đang cập nhật...' : 'Đã chuyển khoản'}
                        </button>
                      ) : (
                        <span className="text-xs text-foreground-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal xem ảnh chứng từ */}
      {proofUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setProofUrl(null)}>
          <div className="bg-background-100 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground-950">Ảnh chứng từ chuyển khoản</h3>
              <button onClick={() => setProofUrl(null)} className="text-foreground-600 hover:text-foreground-900 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <img src={proofUrl.url} alt="Chứng từ chuyển khoản" className="w-full rounded-lg object-contain" />
          </div>
        </div>
      )}

      {/* Modal từ chối */}
      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setRejecting(null)}>
          <div className="bg-background-100 rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-foreground-950 mb-3">Lý do từ chối</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Nhập lý do từ chối..."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejecting(null)} className="px-4 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm font-semibold text-foreground-700 hover:bg-background-200/60 cursor-pointer whitespace-nowrap">
                Hủy
              </button>
              <button
                onClick={() => {
                  const request = requests.find((r) => r.id === rejecting);
                  if (request) handleReject(request);
                }}
                className="px-4 py-2 rounded-md bg-rose-500 text-background-950 text-sm font-semibold hover:bg-rose-600 cursor-pointer whitespace-nowrap"
              >
                Xác nhận từ chối
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}